import { useEffect, useMemo, useState } from 'react';
import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import { SortBy } from 'tea-component/lib/table/addons';
import { getCreativeTrendRelatedCreatives } from '@opm/services';
import { useStableValue } from '@opm/utils/stableKey';
import { ICreativeItem } from '../types';
import { DEFAULT_PAGE_INFO } from '../constants';

/**
 * 详情页 Related Creatives 数据 Hook（双模式）
 *
 * - Card 模式：下拉加载（infinite scroll），累积所有页数据
 * - Table 模式：分页器翻页，只取当前页数据
 * - 视图切换、筛选条件、排序、搜索切换时：清空数据，重置 page:1 重新请求
 */
export function useRelatedCreatives({
  filter,
  defaultPageInfo = DEFAULT_PAGE_INFO,
  defaultViewMode = 'card',
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  defaultViewMode?: 'card' | 'table';
}) {
  // ─── 视图模式 ───
  const [viewMode, setViewMode] = useState<'card' | 'table'>(defaultViewMode);

  // ─── 搜索关键字 ───
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');

  // ─── AI Tags 搜索（分号分隔），与 Competitors 主页一致 ───
  const [searchVideoTagsRef, searchVideoTags, setSearchVideoTags] = useStateRef<
    string[]
  >([]);

  // ─── 排序 ───
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'impression_number',
  });

  // ─── 分页 ───
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({ defaultPageInfo });

  // ─── 数据存储 ───
  const [cardDataRef, cardRecords, setCardData] = useStateRef<ICreativeItem[]>(
    [],
  );
  const [tableRecords, setTableRecords] = useState<ICreativeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [maxStats, setMaxStats] = useState<Record<string, number>>({});

  // ─── 请求 ───
  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook({
    clear: () => {
      setTableRecords([]);
      setTotal(0);
      setMaxStats({});
    },
  });

  function resetAllData() {
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    setMaxStats({});
    resetPageInfo();
  }

  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
  }

  function changeKeyword(val: string) {
    if (val === keywordRef.current) return;
    setKeyword(val);
  }

  // ─── 切换 AI Tags 搜索 ───
  function changeSearchVideoTags(val: string[]) {
    setSearchVideoTags(val);
  }

  function changeViewMode(mode: 'card' | 'table') {
    if (mode === viewMode) return;
    setViewMode(mode);
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    resetPageInfo();
  }

  function loadNextPage() {
    if (loadingRef.current) return;
    if (viewMode !== 'card') return;
    if (total === 0) return;
    const loadedCount = cardDataRef.current?.length || 0;
    if (loadedCount >= total && total > 0) return;
    nextPage();
  }

  function changeTablePageInfo(newPageInfo?: {
    page: number;
    page_size: number;
  }) {
    setTableRecords([]);
    changePageInfo(newPageInfo);
  }

  const stableFilter = useStableValue(filter);
  const stablePageInfo = useStableValue(pageInfo);

  useUpdateEffect(() => {
    if (!stableFilter) return;
    resetAllData();
  }, [stableFilter]);

  const params = useMemo(() => {
    if (!stableFilter?.game_id || !stableFilter?.topic) return null;
    return {
      ...stableFilter,
      ...stablePageInfo,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
      search_video_tags: searchVideoTagsRef.current,
    };
  }, [stableFilter, stablePageInfo, sortBy, keyword, searchVideoTags]);

  function request() {
    if (!params) return;
    wrapperCancelRequest(getCreativeTrendRelatedCreatives, params)
      .then((response: any) => {
        const { list = [], total: totalCount = 0, max = {} } = response || {};

        if (viewMode === 'card') {
          if (pageInfo.page === 1) {
            setCardData(list);
          } else {
            setCardData([...(cardDataRef.current || []), ...list]);
          }
        } else {
          setTableRecords(list);
        }

        setTotal(totalCount);
        setMaxStats(max);
      })
      .catch(() => {
        if (viewMode === 'card' && pageInfo.page > 1) {
          const prevPage = Math.max(
            1,
            Math.ceil((cardDataRef.current?.length || 0) / pageInfo.page_size),
          );
          changePageInfoNoRender({ ...pageInfo, page: prevPage });
        }
      });
  }

  useEffect(() => {
    if (!params) return;
    request();
  }, [params, viewMode]);

  const currentRecords = viewMode === 'card' ? cardRecords || [] : tableRecords;
  const hasMore = (cardRecords?.length || 0) < total;
  const isEmpty = !loading && currentRecords.length === 0;
  const isFirstLoading =
    loading && pageInfo.page === 1 && currentRecords.length === 0;
  const isPaginationLoading =
    loading && viewMode === 'card' && (cardRecords?.length || 0) > 0;

  return {
    cardRecords: cardRecords || [],
    tableRecords,
    total,
    maxStats,
    hasMore,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    sortBy: sortBy as SortBy<string>,
    setSortBy: changeSortBy,
    keyword: keyword || '',
    setKeyword: changeKeyword,
    // AI Tags 搜索（与 Competitors 主页一致）
    searchVideoTags: searchVideoTags || [],
    setSearchVideoTags: changeSearchVideoTags,
    viewMode,
    setViewMode: changeViewMode,
  };
}
